import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Preferences } from '@capacitor/preferences';
import {
  HeadingRegular,
  BodyR,
  Button,
  DetailB,
} from '@pensionbee/honeypot-ui';
import HeaderlessMobileLayout from '@/domains/shared/components/layouts/HeaderlessMobileLayout';
import type { ReactElement } from 'react';
interface PinEntryFormProps {
  onSuccess: () => void;
  onIncorrectPin: () => void;
  onForgotPin: () => void;
  incorrectAttempts: number;
}

const PinEntry = ({
  onSuccess,
  onIncorrectPin,
  onForgotPin,
  incorrectAttempts,
}: PinEntryFormProps) => {
  const [pinDigits, setPinDigits] = useState<string[]>(['', '', '', '']);
  const [error, setError] = useState('');
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [revealIndex, setRevealIndex] = useState<number | null>(null);
  const submittingRef = useRef(false);

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const validatePin = async (pin: string) => {
    const { value: savedPin } = await Preferences.get({ key: 'PIN_KEY' });

    if (pin.length !== 4) {
      setError('The PIN must be 4 digits long.');
      return false;
    }
    if (pin !== savedPin) {
      const attemptsLeft = 2 - incorrectAttempts;
      setError(
        `The PIN you entered is incorrect. You have ${attemptsLeft} more attempt${attemptsLeft === 1 ? '' : 's'}.`,
      );
      return false;
    }
    return true;
  };

  const resetPin = () => {
    setPinDigits(['', '', '', '']);
    inputRefs.current[0]?.focus();
  };

  const handleSubmit = useCallback(async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;

    const pin = pinDigits.join('');
    const isValid = await validatePin(pin);

    if (!isValid) {
      onIncorrectPin();
      resetPin();
      submittingRef.current = false;
      return;
    }

    submittingRef.current = false;
    onSuccess();
  }, [pinDigits, onIncorrectPin, onSuccess]);

  const handleDigitChange = (
    index: number,
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const digit = e.target.value.replace(/\D/g, '').slice(-1);

    const next = [...pinDigits];
    next[index] = digit;
    setPinDigits(next);
    setError('');

    if (digit) {
      setRevealIndex(index);
      setTimeout(() => setRevealIndex(null), 500);
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === 'Backspace' && !pinDigits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const digits = e.clipboardData
      .getData('text')
      .replace(/\D/g, '')
      .slice(0, 4)
      .split('');

    if (!digits.length) return;

    setPinDigits(digits.concat(Array(4 - digits.length).fill('')));
    setError('');

    const lastIndex = Math.min(digits.length - 1, 3);
    setRevealIndex(lastIndex);
    setTimeout(() => setRevealIndex(null), 500);

    inputRefs.current[lastIndex]?.focus();
  };

  useEffect(() => {
    const pin = pinDigits.join('');
    if (pin.length === 4) {
      // Automatically submit when 4 digits are entered
      handleSubmit();
    }
  }, [pinDigits, handleSubmit]);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit();
      }}
    >
      <div className="mx-auto max-w-header grid grid-cols-12 pt-40 px-20">
        <div className="mt-96 col-span-12 md:col-span-6 md:col-start-4">
          <HeadingRegular>Please enter your PIN</HeadingRegular>

          <div className="pt-28 flex justify-between gap-12 max-w-md">
            {pinDigits.map((digit, i) => (
              <input
                key={i}
                ref={(el) => {
                  inputRefs.current[i] = el;
                }}
                type={revealIndex === i ? 'text' : 'password'}
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={1}
                value={digit}
                onChange={(e) => handleDigitChange(i, e)}
                onKeyDown={(e) => handleKeyDown(i, e)}
                onPaste={handlePaste}
                aria-label={`PIN digit ${i + 1}`}
                className="text-center text-24 font-bold bg-grey-8 border-2 border-solid border-grey-3 rounded-8 focus:outline-none focus:border-blue-100"
                style={{
                  width: '72px',
                  height: '64px',
                }}
              />
            ))}
          </div>
          <div className="pt-16">
            <BodyR
              as="button"
              type="button"
              onClick={onForgotPin}
              className="text-blue-100 underline"
            >
              Forgot PIN?
            </BodyR>
            {!!error && (
              <div className="mt-20 bg-red-10 rounded-12 p-20">
                <DetailB className="text-red-140">{error}</DetailB>
              </div>
            )}
          </div>
          <div className="pt-28">
            <Button type="submit" className="w-full">
              Confirm
            </Button>
          </div>
        </div>
      </div>
    </form>
  );
};

export default PinEntry;

PinEntry.getLayout = function getLayout(page: ReactElement) {
  return <HeaderlessMobileLayout>{page}</HeaderlessMobileLayout>;
};
